import { TType } from 'ts-interface-checker';
import { EventFlow, EventType } from '../dto/events';
import {
  ClientBookAppointment,
  ClientRejectMatches,
  PatientSchedulerCreateFields,
  PatientSchedulerOpenFields,
  PatientSchedulerRequestFields,
  SoharApiResponseWebhookFields,
  SoharVerificationAppointmentRecheckFields,
  SoharVerificationIntakeFields,
  SoharVerificationZocDocFields,
  SoharVerificationZocDocRecheckFields,
  UnsetEventSpecification,
} from '../generated/dto/events/index-ti';

/**
 * Maps a particular event (defined by its layer and flow)
 * to the associated type checker from (`/generated/dto/events/index-ti`)
 * Note: Please keep this in alphabetical order.
 * Note 2: Use `UnsetEventSpecification` if you have not yet agreed
 * on an event spec with the Data team.
 */
export const eventDetailMapping: {
  [e in EventFlow]?: { [f in EventType]?: TType };
} = {
  book_appointment: {
    healow: UnsetEventSpecification,
    client: ClientBookAppointment,
  },
  clear_assessment_response: {
    command: UnsetEventSpecification,
  },
  ecw_snowflake_file_sync: {
    api_call: UnsetEventSpecification,
  },
  ecw_snowflake_patient_insurance: {
    api_call: UnsetEventSpecification,
  },
  ecw_snowflake_provider_facilities_sync: {
    api_call: UnsetEventSpecification,
  },
  get_active_patient: {
    query: UnsetEventSpecification,
  },
  get_active_patients: {
    query: UnsetEventSpecification,
  },
  get_active_patients_by_mrns: {
    query: UnsetEventSpecification,
  },
  get_provider_matches: {
    query: UnsetEventSpecification,
  },
  fetch_schedule_id: {
    healow: UnsetEventSpecification,
  },
  fetch_timeslots_for_schedule_id: {
    healow: UnsetEventSpecification,
  },
  intake__questions__answer_question: {
    command: UnsetEventSpecification,
    client: UnsetEventSpecification,
  },
  intake__patient_auth__authenticate_from_email_link: {
    client: UnsetEventSpecification,
  },
  intake__patient_auth__submit_email: {
    api_call: UnsetEventSpecification,
    client: UnsetEventSpecification,
  },
  intake_submit: {
    command: UnsetEventSpecification,
    client: UnsetEventSpecification,
  },
  patient_scheduler__create_schedule_request: {
    command: PatientSchedulerCreateFields,
  },
  patient_scheduler__open_schedule_request: {
    command: PatientSchedulerOpenFields,
  },
  patient_scheduler__request_appointment: {
    command: PatientSchedulerRequestFields,
  },
  portal__cognito_barnard_user_conversion: {
    scripts: UnsetEventSpecification,
  },
  portal__cognito_user_sync: {
    scripts: UnsetEventSpecification,
  },
  portal__convert_user: {
    api_call: UnsetEventSpecification,
  },
  portal__create_user: {
    api_call: UnsetEventSpecification,
  },
  portal__update_user: {
    api_call: UnsetEventSpecification,
  },
  receive_sohar_api_response_webhook: {
    command: SoharApiResponseWebhookFields,
  },
  referrals__generate_link: {
    command: UnsetEventSpecification,
  },
  referrals__specify_consent_for_sharing: {
    command: UnsetEventSpecification,
    client: UnsetEventSpecification,
  },
  reject_matches: {
    client: ClientRejectMatches,
  },
  send_sohar_verification_established_patient_request_appointment_recheck: {
    api_call: SoharVerificationAppointmentRecheckFields,
  },
  send_sohar_verification_established_patient_recheck_zocdoc: {
    api_call: SoharVerificationZocDocRecheckFields,
  },
  send_sohar_verification_request_intakes: {
    api_call: SoharVerificationIntakeFields,
  },
  send_sohar_verification_request_zocdoc: {
    api_call: SoharVerificationZocDocFields,
  },
  sync_ecw_patient_pharmacy_from_snowflake: {
    api_call: UnsetEventSpecification,
    scripts: UnsetEventSpecification,
  },
  sync_patient_medications_from_snowflake: {
    api_call: UnsetEventSpecification,
    scripts: UnsetEventSpecification,
  },
  sync_ehr_patient_from_snowflake: {
    api_call: UnsetEventSpecification,
    scripts: UnsetEventSpecification,
  },
  sync_ehr_patient_from_snowflake_batch: {
    api_call: UnsetEventSpecification,
    scripts: UnsetEventSpecification,
  },
  sync_provider_email_from_snowflake: {
    api_call: UnsetEventSpecification,
    scripts: UnsetEventSpecification,
  },
  undo_insurance_response: {
    client: UnsetEventSpecification,
  },
  update_document: {
    command: UnsetEventSpecification,
  },
  upload_file: {
    command: UnsetEventSpecification,
  },
  update_ehr_patient_malformed_phone: {
    api_call: UnsetEventSpecification,
    scripts: UnsetEventSpecification,
  },
};

// Controls which event layers can get sent to the event-service/MQ
export const LAYER_EVENT_SEND_ALLOW_LIST: EventType[] = [
  'client',
  'command',
  'healow',
  'query',
  'api_call',
];
